import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, Edit, Trash2, Eye, EyeOff, ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import { useI18n } from '../hooks/useI18n';
import { hasPermission, PERMISSIONS } from "../utils/authUtils";
import { agentAPI, companyAPI } from "../services/api";
import { Agent, CreateAgentRequest, Company } from "../types";

const initAgentCreation = ()=>{
  return {
      email: '',
      login: '',
      password: '',
      allowedCompanyIds: [],
      canCreateClients: false,
      canCreateCompanies: false,
      canCreateInvoices: true,
      canEditClients: false,
      canEditCompanies: false,
      canEditInvoices: false,
      canDeleteClients: false,
      canDeleteCompanies: false,
      canDeleteInvoices: false,
      canCreateOrders: false,
      canEditOrders: false,
      canDeleteOrders: false,
      canCreateSuppliers: false,
      canEditSuppliers: false,
      canDeleteSuppliers: false,
      canViewAllCompanies: false,
  }
}

const AgentManagementPage: React.FC = () => {
  const { t, isRTL } = useI18n();
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [formData, setFormData] = useState<CreateAgentRequest>(initAgentCreation());

  useEffect(() => {
    fetchAgents();
    fetchCompanies();
  }, []);

  const fetchAgents = async () => {
      const data = await agentAPI.getAllAgents();
      setAgents(data);
      setLoading(false);
  };

  const fetchCompanies = async () => {
      setCompaniesLoading(true);
      const companyList = await companyAPI.getAllCompanies()
      setCompanies(companyList);
      setCompaniesLoading(false);
  };

  const handleCreateAgent = async (e: React.FormEvent) => {
    setShowCreateModal(false)
    await agentAPI.createAgent(formData)
    setFormData(initAgentCreation());
    fetchAgents();
  };

  const handleEditAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAgent) return;
    await agentAPI.updateAgent(selectedAgent.id, formData)

    setShowEditModal(false);
    setSelectedAgent(null);
    fetchAgents();
  };

  const handleDeactivateAgent = async (agentId: string) => {
    if (!window.confirm('Are you sure you want to deactivate this agent?')) {
      return;
    }
    agentAPI.deleteAgent(agentId);
    fetchAgents();
  };

  const openEditModal = (agent: Agent) => {

    setSelectedAgent(agent);
    setFormData({
      email: agent.email,
      login: agent.login,
      password: '',
      allowedCompanyIds: agent.allowedCompanyIds,
      canCreateClients: agent.canCreateClients,
      canCreateCompanies: agent.canCreateCompanies,
      canCreateInvoices: agent.canCreateInvoices,
      canEditClients: agent.canEditClients,
      canEditCompanies: agent.canEditCompanies,
      canEditInvoices: agent.canEditInvoices,
      canDeleteClients: agent.canDeleteClients,
      canDeleteCompanies: agent.canDeleteCompanies,
      canDeleteInvoices: agent.canDeleteInvoices,
      canCreateOrders: agent.canCreateOrders,
      canEditOrders: agent.canEditOrders,
      canDeleteOrders: agent.canDeleteOrders,
      canCreateSuppliers: agent.canCreateSuppliers,
      canEditSuppliers: agent.canEditSuppliers,
      canDeleteSuppliers: agent.canDeleteSuppliers,
      canViewAllCompanies: agent.canViewAllCompanies
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`min-h-screen ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
            <button
              onClick={() => navigate('/dashboard')}
              className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} text-gray-600 hover:text-gray-900 transition-colors`}
            >
              <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <span>{t('agentManagement.backToDashboard')}</span>
            </button>
          </div>
          <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
            <Users className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">{t('agentManagement.title')}</h1>
          </div>
        </div>

        {/* Create Agent Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowCreateModal(true)}
            className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'} bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors`}
          >
            <Plus className="w-4 h-4" />
            <span>{t('agentManagement.createAgent')}</span>
          </button>
        </div>

        {/* Agents List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{t('agentManagement.yourAgents')}</h2>
          </div>

          {agents.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('agentManagement.noAgentsYet')}</h3>
              <p className="text-gray-600 mb-4">{t('agentManagement.createFirstAgent')}</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                {t('agentManagement.createAgentButton')}
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`px-6 py-3 text-${isRTL ? 'right' : 'left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t('agentManagement.form.login')} / {t('agentManagement.form.email')}</th>
                    <th className={`px-6 py-3 text-${isRTL ? 'right' : 'left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t('agentManagement.status.active')}</th>
                    <th className={`px-6 py-3 text-${isRTL ? 'right' : 'left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t('agentManagement.permissions')}</th>
                    <th className={`px-6 py-3 text-${isRTL ? 'right' : 'left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t('agentManagement.companies')}</th>
                    <th className={`px-6 py-3 text-${isRTL ? 'right' : 'left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>{t('agentManagement.actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agents.map((agent) => (
                    <tr key={agent.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">{agent.login}</span>
                          <span className="text-sm text-gray-500">{agent.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${agent.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                          {agent.active ? t('agentManagement.status.active') : t('agentManagement.status.inactive')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-wrap gap-1">
                          {agent.canCreateClients && <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{t('agentManagement.permissionLabels.createClients')}</span>}
                          {agent.canEditClients && <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">{t('agentManagement.permissionLabels.editClients')}</span>}
                          {agent.canDeleteClients && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{t('agentManagement.permissionLabels.deleteClients')}</span>}

                          {agent.canCreateInvoices && <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">{t('agentManagement.permissionLabels.createInvoices')}</span>}
                          {agent.canEditInvoices && <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">{t('agentManagement.permissionLabels.editInvoices')}</span>}
                          {agent.canDeleteInvoices && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{t('agentManagement.permissionLabels.deleteInvoices')}</span>}

                          {agent.canCreateOrders && <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded">{t('agentManagement.permissionLabels.createOrders')}</span>}
                          {agent.canEditOrders && <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">{t('agentManagement.permissionLabels.editOrders')}</span>}
                          {agent.canDeleteOrders && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{t('agentManagement.permissionLabels.deleteOrders')}</span>}

                          {agent.canCreateSuppliers && <span className="px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded">{t('agentManagement.permissionLabels.createSuppliers')}</span>}
                          {agent.canEditSuppliers && <span className="px-2 py-1 bg-cyan-100 text-cyan-800 text-xs rounded">{t('agentManagement.permissionLabels.editSuppliers')}</span>}
                          {agent.canDeleteSuppliers && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{t('agentManagement.permissionLabels.deleteSuppliers')}</span>}

                          {agent.canCreateCompanies && <span className="px-2 py-1 bg-pink-100 text-pink-800 text-xs rounded">{t('agentManagement.permissionLabels.createCompanies')}</span>}
                          {agent.canEditCompanies && <span className="px-2 py-1 bg-rose-100 text-rose-800 text-xs rounded">{t('agentManagement.permissionLabels.editCompanies')}</span>}
                          {agent.canDeleteCompanies && <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">{t('agentManagement.permissionLabels.deleteCompanies')}</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex flex-wrap gap-1">
                          {agent.allowedCompanyIds.length > 0 ? (
                            agent.allowedCompanyIds.map(companyId => {
                              const company = companies.find(c => c.id === companyId);
                              return company ? <span key={companyId} className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">{company.name}</span> : null;
                            })
                          ) : (
                            agent.canViewAllCompanies ? <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">{t('agentManagement.form.viewAllCompanies')}</span> : <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">{t('agentManagement.form.noCompaniesAvailable')}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                          <button
                            onClick={()=> {
                              openEditModal(agent)
                            }}
                            className="text-blue-600 hover:text-blue-900 flex items-center"
                            title={t('agentManagement.edit')}
                          >
                            <Edit className="w-4 h-4" />
                            {t('agentManagement.edit')}
                          </button>
                          <button
                            onClick={() => handleDeactivateAgent(agent.id)}
                            className="text-red-600 hover:text-red-900 flex items-center"
                            title={t('agentManagement.deactivate')}
                          >
                            <Trash2 className="w-4 h-4" />
                            {t('agentManagement.deactivate')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Agent Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[34rem] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('agentManagement.createModal.title')}</h3>
                <form onSubmit={handleCreateAgent} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('agentManagement.form.email')}</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('agentManagement.form.login')}</label>
                    <input
                      type="text"
                      required
                      value={formData.login}
                      onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('agentManagement.form.password')}</label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Company Access */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">{t('agentManagement.form.companyAccess')}</h4>
                    <label className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        checked={formData.canViewAllCompanies}
                        onChange={(e) => setFormData({ ...formData, canViewAllCompanies: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm">{t('agentManagement.form.viewAllCompanies')}</span>
                    </label>

                    {!formData.canViewAllCompanies && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('agentManagement.form.selectSpecificCompanies')} ({companies.length} available):
                        </label>
                        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          {companiesLoading ? (
                            <div className="text-sm text-gray-500 p-2">{t('agentManagement.form.loadingCompanies')}</div>
                          ) : companies.length === 0 ? (
                            <div className="text-sm text-gray-500 p-2">{t('agentManagement.form.noCompaniesAvailable')}</div>
                          ) : (
                            companies.map((company) => (
                              <label key={company.id} className="flex items-center mb-1">
                                <input
                                  type="checkbox"
                                  checked={formData.allowedCompanyIds.includes(company.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        allowedCompanyIds: [...formData.allowedCompanyIds, company.id]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        allowedCompanyIds: formData.allowedCompanyIds.filter(id => id !== company.id)
                                      });
                                    }
                                  }}
                                  className="mr-2"
                                />
                                <span className="text-sm">{company.name} ({company.ice})</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Permissions */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">{t('agentManagement.permissions')}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canCreateClients}
                          onChange={(e) => setFormData({ ...formData, canCreateClients: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.createClients')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canEditClients}
                          onChange={(e) => setFormData({ ...formData, canEditClients: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.editClients')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canDeleteClients}
                          onChange={(e) => setFormData({ ...formData, canDeleteClients: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.deleteClients')}</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canCreateInvoices}
                          onChange={(e) => setFormData({ ...formData, canCreateInvoices: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.createInvoices')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canEditInvoices}
                          onChange={(e) => setFormData({ ...formData, canEditInvoices: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.editInvoices')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canDeleteInvoices}
                          onChange={(e) => setFormData({ ...formData, canDeleteInvoices: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.deleteInvoices')}</span>
                      </label>
                      {hasPermission(PERMISSIONS.STOCK_FEATURE) && (<>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.canCreateOrders}
                            onChange={(e) => setFormData({ ...formData, canCreateOrders: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm">{t('agentManagement.permissionLabels.createOrders')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.canEditOrders}
                            onChange={(e) => setFormData({ ...formData, canEditOrders: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm">{t('agentManagement.permissionLabels.editOrders')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.canDeleteOrders}
                            onChange={(e) => setFormData({ ...formData, canDeleteOrders: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm">{t('agentManagement.permissionLabels.deleteOrders')}</span>
                        </label>
                      </>)}
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canCreateSuppliers}
                          onChange={(e) => setFormData({ ...formData, canCreateSuppliers: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.createSuppliers')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canEditSuppliers}
                          onChange={(e) => setFormData({ ...formData, canEditSuppliers: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.editSuppliers')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canDeleteSuppliers}
                          onChange={(e) => setFormData({ ...formData, canDeleteSuppliers: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.deleteSuppliers')}</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canCreateCompanies}
                          onChange={(e) => setFormData({ ...formData, canCreateCompanies: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.createCompanies')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canEditCompanies}
                          onChange={(e) => setFormData({ ...formData, canEditCompanies: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.editCompanies')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canDeleteCompanies}
                          onChange={(e) => setFormData({ ...formData, canDeleteCompanies: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.deleteCompanies')}</span>
                      </label>
                    </div>
                  </div>

                  <div className={`flex justify-end ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {t('agentManagement.form.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                    >
                      {t('agentManagement.form.create')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Edit Agent Modal */}
        {showEditModal && selectedAgent && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-[34rem] shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t('agentManagement.editModal.title')}</h3>
                <form onSubmit={handleEditAgent} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('agentManagement.form.email')}</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('agentManagement.form.login')}</label>
                    <input
                      type="text"
                      required
                      value={formData.login}
                      onChange={(e) => setFormData({ ...formData, login: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('agentManagement.form.newPasswordOptional')}</label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={t('agentManagement.form.passwordPlaceholder')}
                    />
                  </div>

                  {/* Company Access */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">{t('agentManagement.form.companyAccess')}</h4>
                    <label className="flex items-center mb-3">
                      <input
                        type="checkbox"
                        checked={formData.canViewAllCompanies}
                        onChange={(e) => setFormData({ ...formData, canViewAllCompanies: e.target.checked })}
                        className="mr-2"
                      />
                      <span className="text-sm">{t('agentManagement.form.viewAllCompanies')}</span>
                    </label>

                    {!formData.canViewAllCompanies && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('agentManagement.form.selectSpecificCompanies')} ({companies.length} available):
                        </label>
                        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          {companiesLoading ? (
                            <div className="text-sm text-gray-500 p-2">{t('agentManagement.form.loadingCompanies')}</div>
                          ) : companies.length === 0 ? (
                            <div className="text-sm text-gray-500 p-2">{t('agentManagement.form.noCompaniesAvailable')}</div>
                          ) : (
                            companies.map((company) => (
                              <label key={company.id} className="flex items-center mb-1">
                                <input
                                  type="checkbox"
                                  checked={formData.allowedCompanyIds.includes(company.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        allowedCompanyIds: [...formData.allowedCompanyIds, company.id]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        allowedCompanyIds: formData.allowedCompanyIds.filter(id => id !== company.id)
                                      });
                                    }
                                  }}
                                  className="mr-2"
                                />
                                <span className="text-sm">{company.name} ({company.ice})</span>
                              </label>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Permissions */}
                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-900">{t('agentManagement.permissions')}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canCreateClients}
                          onChange={(e) => setFormData({ ...formData, canCreateClients: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.createClients')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canEditClients}
                          onChange={(e) => setFormData({ ...formData, canEditClients: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.editClients')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canDeleteClients}
                          onChange={(e) => setFormData({ ...formData, canDeleteClients: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.deleteClients')}</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canCreateInvoices}
                          onChange={(e) => setFormData({ ...formData, canCreateInvoices: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.createInvoices')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canEditInvoices}
                          onChange={(e) => setFormData({ ...formData, canEditInvoices: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.editInvoices')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canDeleteInvoices}
                          onChange={(e) => setFormData({ ...formData, canDeleteInvoices: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.deleteInvoices')}</span>
                      </label>

                      {hasPermission(PERMISSIONS.STOCK_FEATURE) && (<>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.canCreateOrders}
                            onChange={(e) => setFormData({ ...formData, canCreateOrders: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm">{t('agentManagement.permissionLabels.createOrders')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.canEditOrders}
                            onChange={(e) => setFormData({ ...formData, canEditOrders: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm">{t('agentManagement.permissionLabels.editOrders')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.canDeleteOrders}
                            onChange={(e) => setFormData({ ...formData, canDeleteOrders: e.target.checked })}
                            className="mr-2"
                          />
                          <span className="text-sm">{t('agentManagement.permissionLabels.deleteOrders')}</span>
                        </label>
                      </>)}
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canCreateSuppliers}
                          onChange={(e) => setFormData({ ...formData, canCreateSuppliers: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.createSuppliers')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canEditSuppliers}
                          onChange={(e) => setFormData({ ...formData, canEditSuppliers: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.editSuppliers')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canDeleteSuppliers}
                          onChange={(e) => setFormData({ ...formData, canDeleteSuppliers: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.deleteSuppliers')}</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canCreateCompanies}
                          onChange={(e) => setFormData({ ...formData, canCreateCompanies: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.createCompanies')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canEditCompanies}
                          onChange={(e) => setFormData({ ...formData, canEditCompanies: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.editCompanies')}</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.canDeleteCompanies}
                          onChange={(e) => setFormData({ ...formData, canDeleteCompanies: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">{t('agentManagement.permissionLabels.deleteCompanies')}</span>
                      </label>
                    </div>
                  </div>

                  <div className={`flex justify-end ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {t('agentManagement.form.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                    >
                      {t('agentManagement.form.update')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout >
  );
};

export default AgentManagementPage;
